/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Spin,
  Modal,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

// utils/apis/constants
import {
  getCalculosAnualesISRImportes,
  getCalculoAnualISRImporte,
  postCalculoAnualISRImporte,
  patchCalculoAnualISRImporte,
  deleteCalculoAnualISRImporte,
} from 'api/nominas/calculoISRAnual/isrCalculosAnualesImportes';
import { checkToken } from 'api/utils';
import { getEstadosGlobales } from 'api/catalogos';
import { formatReceivedValues } from 'utils/formatValues';
import currencyFormatter from 'utils/currencyFormatter';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { onError, onSuccess } from 'utils/handlers';
import moment from 'moment';
import {
  ESTATUS_ACTIVO,
  ESTATUS_CANCELADO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';

// Componentes
import Table from 'components/Table';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import CurrencyInput from 'components/CurrencyInput';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';

export const importeAnualISRPermission = {
  permissionModel: 'isrimportecalculoanual',
  catalogsModels: [],
  tabsModels: [],
};

const ImporteAnualISR = ({ permission }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState();
  const [estados, setEstados] = useState([]);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [confirmationVisibleAutorizar, setConfirmationVisibleAutorizar] = useState(false);
  const [confirmationVisibleEstatus, setConfirmationVisibleEstatus] = useState(false);
  const [confirmationVisibleCancelar, setConfirmationVisibleCancelar] = useState(false);
  const [estatusInactivo, setEstatusInactivo] = useState(false);
  const [estatusAutorizado, setEstatusAutorizado] = useState(false);
  const [estatusCancelado, setEstatusCancelado] = useState(false);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [estadosFiltered, setEstadosFiltered] = useState([]);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    const _data = await getCalculosAnualesISRImportes();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _periodosFiscales,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getCalculosAnualesISRImportes(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setPeriodosFiscales(_periodosFiscales);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowData();
    setSelectedRowKeys([]);
    form.resetFields();
    setVisible(false);
    setEstatusInactivo(false);
    setEstatusAutorizado(false);
    setEstatusCancelado(false);
  };

  const onClickAdd = () => {
    onCancel();
    setEstadosFiltered(estados);
    form.setFieldValue('estados_globales', 1);
    setVisible(true);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _calculoAnualIsrImporte = await getCalculoAnualISRImporte(match.id);
    if (_calculoAnualIsrImporte?.estados_globales === ESTATUS_ACTIVO
      || _calculoAnualIsrImporte?.estados_globales === ESTATUS_INACTIVO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_INACTIVO
      || estado?.id === ESTATUS_ACTIVO));
    }
    if (_calculoAnualIsrImporte?.estados_globales === ESTATUS_AUTORIZADO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_AUTORIZADO));
      setEstatusAutorizado(true);
    }
    if (_calculoAnualIsrImporte?.estados_globales === ESTATUS_INACTIVO) {
      setEstatusInactivo(true);
    }
    if (_calculoAnualIsrImporte?.estados_globales === ESTATUS_CANCELADO) {
      setEstadosFiltered(estados?.filter((estado) => estado?.id === ESTATUS_CANCELADO));
      setEstatusCancelado(true);
    }
    setTimeout(() => {
      if (_calculoAnualIsrImporte) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedValues(_calculoAnualIsrImporte));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchCalculoAnualISRImporte(key, values) : postCalculoAnualISRImporte(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const deleteItem = async () => {
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteCalculoAnualISRImporte(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const contentDelete = () => {
    const ano = periodosFiscales?.find((p) => p?.id === selectedRowData?.periodo_fiscal)?.descripcion;
    return `Año: ${ano || ''} Importe: $${selectedRowData?.importe}`;
  };

  const handleContentPrint = () => {
    const ano = periodosFiscales?.find((p) => p?.id === selectedRowData?.periodo_fiscal)?.descripcion;
    return `Año: ${ano || ''} Importe: $${selectedRowData?.importe}`;
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una nómina ya autorizada');
        return;
      }
      const fecha_calculo = moment(selectedRowData?.fecha_de_calculo, 'YYYY-MM-DD');
      const vals = {
        ...selectedRowData,
        fecha_de_calculo: fecha_calculo,
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchCalculoAnualISRImporte(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisibleAutorizar(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar una nómina ya cancelada');
        return;
      }
      const fecha_calculo = moment(selectedRowData?.fecha_de_calculo, 'YYYY-MM-DD');
      const vals = {
        ...selectedRowData,
        fecha_de_calculo: fecha_calculo,
        estados_globales: ESTATUS_CANCELADO,
      };
      const res = await patchCalculoAnualISRImporte(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisibleCancelar(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    setConfirmationVisibleEstatus(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedRowData(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Año',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 200,
      render: (val) => periodosFiscales?.find((periodo) => periodo?.id === val)?.descripcion,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 200,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados?.find((estado) => estado?.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="...Cargando" spinning={loading}>
        <Table
          data={data}
          cols={columns}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setConfirmationVisibleAutorizar(true),
              permission: { model: 'isrimportecalculoanual', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || selectedRowData?.estados_globales === ESTATUS_AUTORIZADO
                || selectedRowData?.estados_globales === ESTATUS_CANCELADO
                || selectedRowData?.estados_globales === ESTATUS_INACTIVO,
            },
            {
              permission: { model: 'isrimportecalculoanual', action: 'cancelar' },
              onClick: () => setConfirmationVisibleCancelar(true),
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO,
            },
          ]}
          disableDelete={selectedRowData?.estados_globales === ESTATUS_AUTORIZADO
          || selectedRowData?.estados_globales === ESTATUS_INACTIVO}
          selectedRowKeys={selectedRowKeys}
          handleOnRowClick={handleOnRowClick}
          rowSelection={rowSelection}
          permission={permission}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label="Consulta de Cálculo"
              onCancel={onCancel}
              onFinish={onFinish}
              disableOnFinish={selectedRowData?.estados_globales === ESTATUS_AUTORIZADO
              || selectedRowData?.estados_globales === ESTATUS_INACTIVO}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="45%"
        >
          <Form
            layout="vertical"
            form={form}
            scrollToFirstError
            name="general"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onFinish();
              }
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                label="Año"
                name="periodo_fiscal"
                rules={rules.required}
              >
                <Select
                  dataSource={periodosFiscales}
                  disabled={estatusAutorizado || estatusInactivo || estatusCancelado}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                label="Importe"
                name="importe"
                rules={rules.required}
              >
                <CurrencyInput
                  disabled={estatusAutorizado || estatusInactivo || estatusCancelado}
                />
              </Form.Item>
            </Col>
            { selectedRowKeys?.length
              ? (
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    name="estados_globales"
                    label="Estatus"
                    rules={rules.required}
                  >
                    <Select
                      dataSource={estadosFiltered}
                      disabled={!selectedRowKeys?.length
                      || estatusAutorizado || estatusInactivo || estatusCancelado}
                      onChange={(val) => {
                        if (val === ESTATUS_INACTIVO) {
                          setConfirmationVisibleEstatus(true);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              )
              : (
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    name="estados_globales"
                    label="Estatus"
                    rules={rules.required}
                    initialValue={1}
                  >
                    <Select
                      dataSource={estadosFiltered}
                      disabled
                    />
                  </Form.Item>
                </Col>
              )}
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={contentDelete()}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          onFinishText="Continuar"
          onCancel={() => {
            setConfirmationVisibleAutorizar(false);
          }}
          open={confirmationVisibleAutorizar}
          title="Confirmación"
          contentTitle={`Autorizar el ${handleContentPrint()}`}
          ConfirmAutorize
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationCancelar}
          onFinishText="Continuar"
          onCancel={() => {
            setConfirmationVisibleCancelar(false);
          }}
          open={confirmationVisibleCancelar}
          title="Confirmación"
          contentTitle={`Cancelar el ${handleContentPrint()}`}
          ConfirmCancel
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationEstatus}
          onFinishText="Continuar"
          onCancel={() => {
            setConfirmationVisibleEstatus(false);
            form.setFieldValue('estados_globales', 1);
          }}
          open={confirmationVisibleEstatus}
          title="Confirmación"
          contentTitle={`Inactivar el ${handleContentPrint()}`}
          ConfirmInactive
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ImporteAnualISR;
